import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { catchError, map, Observable, of, shareReplay } from 'rxjs';

export interface ILinkPreview {
  /** Canonical URL (og:url or the final URL after redirects). */
  url: string;
  domain: string;
  siteName?: string;
  title?: string;
  description?: string;
  image?: string;
  favicon?: string;
  type?: string;
  /** False when the card was built from plain HTML instead of og:* tags. */
  hasOpenGraph: boolean;
}

/** Trailing characters that are almost always sentence punctuation, not URL. */
const TRAILING_PUNCTUATION = /[.,;:!?'"”’)\]}>]+$/;

/**
 * Matches an explicit http(s) URL, or a bare `www.`/`domain.tld` style link the
 * way chat apps do. Kept deliberately loose — the server validates for real.
 */
const URL_PATTERN =
  /(?:https?:\/\/|www\.)[^\s<>"']+|\b[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9-]+)*\.(?:com|net|org|io|co|dev|app|uk|us|ai|gg|tv|me|info|biz|edu|gov|news|shop|store|blog|xyz)(?:\/[^\s<>"']*)?/gi;

/**
 * Fetches Open Graph metadata for links pasted into a caption, via the SSR
 * server's `/api/link-preview` endpoint (a browser can't scrape another origin).
 *
 * Results are cached per-URL for the lifetime of the tab so the same link in the
 * composer and then in the feed only costs one round trip.
 */
@Injectable({ providedIn: 'root' })
export class LinkPreviewService {

  private http = inject(HttpClient);
  private platformId = inject(PLATFORM_ID);

  private cache = new Map<string, Observable<ILinkPreview | null>>();
  private static readonly CACHE_MAX = 150;

  /**
   * Returns the first link in `text`, normalised to an absolute http(s) URL,
   * or null when there is none. Skips @mentions and #hashtags by construction —
   * neither can start a match.
   */
  extractFirstUrl(text: string | null | undefined): string | null {
    if (!text) return null;

    URL_PATTERN.lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = URL_PATTERN.exec(text))) {
      // An "@" immediately before the match means it's an email, not a link.
      if (match.index > 0 && text[match.index - 1] === '@') continue;

      const candidate = this.normalise(match[0]);
      if (candidate) return candidate;
    }
    return null;
  }

  /**
   * Every link in `text` with its position — `raw` is the matched text (minus
   * trailing punctuation) and `href` the normalised absolute URL. Used to turn
   * links in plain text (e.g. chat messages) into anchors.
   */
  findUrls(text: string | null | undefined): { index: number; raw: string; href: string }[] {
    if (!text) return [];

    const found: { index: number; raw: string; href: string }[] = [];
    URL_PATTERN.lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = URL_PATTERN.exec(text))) {
      if (match.index > 0 && text[match.index - 1] === '@') continue;

      const href = this.normalise(match[0]);
      if (!href) continue;
      let raw = match[0].replace(TRAILING_PUNCTUATION, '');
      if (raw.endsWith(')') && !raw.includes('(')) raw = raw.slice(0, -1);
      found.push({ index: match.index, raw, href });
    }
    return found;
  }

  /**
   * Same as `extractFirstUrl`, for rich-text fields (event / blog descriptions)
   * whose value is an HTML string rather than plain text.
   */
  extractFirstUrlFromHtml(html: string | null | undefined): string | null {
    if (!html) return null;

    // An anchor the editor already created wins: its href is the exact target,
    // while the visible text might only say "click here".
    const href = /<a\b[^>]*\bhref\s*=\s*["']([^"']+)["']/i.exec(html)?.[1];
    const fromHref = href ? this.normalise(this.decodeEntities(href)) : null;
    if (fromHref) return fromHref;

    return this.extractFirstUrl(this.stripHtml(html));
  }

  private stripHtml(html: string): string {
    const withoutCode = html
      .replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi, ' ')
      .replace(/<[^>]+>/g, ' ');
    return this.decodeEntities(withoutCode);
  }

  /** Just the handful of entities that can appear inside a pasted URL. */
  private decodeEntities(value: string): string {
    return value
      .replace(/&amp;/gi, '&')
      .replace(/&#0*38;/g, '&')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&quot;/gi, '"')
      .replace(/&#0*39;|&apos;/gi, "'")
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>');
  }

  private normalise(raw: string): string | null {
    let value = raw.trim().replace(TRAILING_PUNCTUATION, '');

    // Balance a trailing ")" that belongs to the URL, e.g. a wiki link.
    if (value.endsWith(')') && !value.includes('(')) value = value.slice(0, -1);
    if (!value) return null;

    const withScheme = /^https?:\/\//i.test(value) ? value : `https://${value}`;
    try {
      const url = new URL(withScheme);
      if (!url.hostname.includes('.')) return null;
      return url.toString();
    } catch {
      return null;
    }
  }

  /** Metadata for `url`, or null when the link has no usable preview. */
  get(url: string): Observable<ILinkPreview | null> {
    if (!isPlatformBrowser(this.platformId)) return of(null);

    const cached = this.cache.get(url);
    if (cached) return cached;

    const request$ = this.http
      .get<{ data: ILinkPreview }>('/api/link-preview', {
        params: new HttpParams().set('url', url),
      })
      .pipe(
        map(res => res?.data ?? null),
        catchError(() => of(null)),
        shareReplay({ bufferSize: 1, refCount: false }),
      );

    if (this.cache.size >= LinkPreviewService.CACHE_MAX) {
      const oldest = this.cache.keys().next().value;
      if (oldest !== undefined) this.cache.delete(oldest);
    }
    this.cache.set(url, request$);
    return request$;
  }
}
